import Download from "@mui/icons-material/Download";
import { Button, ButtonProps, Tooltip, Typography } from "@mui/material";
import { JobStatus } from "generated-graphql/graphql";
import { useCheckForFile } from "hooks/useCheckForFile";
import { useCallback, useRef, useState } from "react";
import { useDownloadReportPreview } from "routes/Customer/Chemicals/Report/useDownloadReportPreview";
import { useAlerts } from "./Alerts/AlertProvider";
import { LongPollingJobWrapper, PollingResult } from "./LongPollingJobWrapper";

export function GeneratePdfButton({
  reportId,
  label,
  onComplete,
  ...buttonProps
}: {
  reportId: string;
  label?: string;
  /**
   * Callback to be called when the PDF is generated and downloaded.
   */
  onComplete?: () => void;
} & ButtonProps) {
  const { downloadReportPreview } = useDownloadReportPreview();
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const [reportPdfS3Key, setReportPdfS3Key] = useState<string | undefined>(
    undefined
  );
  const { fetchFile } = useCheckForFile(reportPdfS3Key);
  const alerts = useAlerts();
  const downloadTriggeredRef = useRef<string | undefined>(undefined);

  const handleDownloadClick = useCallback(async () => {
    // clear old job state
    setJobId(undefined);
    setReportPdfS3Key(undefined);
    downloadTriggeredRef.current = undefined; // Reset guard for new job

    const result = await downloadReportPreview(reportId);
    setReportPdfS3Key(result?.s3Key);
    setJobId(result?.jobId);
  }, [downloadReportPreview, reportId]);

  const pollingCompleted = useCallback(
    async (result: PollingResult) => {
      // Guard: Only allow download once per jobId
      // This is kinda hacky but it prevents the file from opening twice.
      if (jobId && downloadTriggeredRef.current === jobId) {
        return;
      }
      if (jobId) {
        downloadTriggeredRef.current = jobId;
      }

      if (!result.pollingTimedOut && result.job?.status === JobStatus.Failed) {
        alerts.error("Failed to generate PDF");
        onComplete?.();
        return;
      }

      if (result.pollingTimedOut) {
        alerts.error("Polling for PDF generation job status timed out");
        onComplete?.();
        return;
      }

      if (result.job?.status === JobStatus.Succeeded) {
        if (
          result.job.output.downloadKeys &&
          result.job.output.downloadKeys.length
        ) {
          for (const s3Key of result.job.output.downloadKeys) {
            const signedUrl = await fetchFile(s3Key);
            if (signedUrl) {
              alerts.success("Preview ready, downloading...");
              const newWindow = window.open(signedUrl, "_blank");

              // If the open fails, i.e. due to a pop-up blocker, provide a link to the preview
              if (
                !newWindow ||
                newWindow.closed ||
                typeof newWindow.closed === "undefined"
              ) {
                // Create temporary download link
                const link = document.createElement("a");
                link.href = signedUrl;
                link.target = "_blank";
                alerts.success(
                  <>
                    Your preview is ready to open.{" "}
                    <a href={signedUrl} target="_blank" rel="noreferrer">
                      View preview
                    </a>
                    <br />
                    <Typography variant="caption">
                      Allow pop-ups from Encamp in your browser to automatically
                      open downloads in the future.
                    </Typography>
                  </>,
                  { autoHideDuration: null }
                );
              }

              onComplete?.();
              return;
            }
          }
        }
      }

      alerts.error("Failed to generate PDF. File not found");
    },
    [jobId, alerts, onComplete, fetchFile]
  );

  return (
    <LongPollingJobWrapper
      key={jobId || "initial"}
      jobId={jobId}
      pollingCompleted={pollingCompleted}
      timeoutInSeconds={1200}
    >
      <Tooltip title="Download a PDF of your report">
        <Button
          startIcon={<Download />}
          color="primary"
          size="small"
          onClick={handleDownloadClick}
          {...buttonProps}
        >
          {label ?? "Report Preview"}
        </Button>
      </Tooltip>
    </LongPollingJobWrapper>
  );
}
